import React, { useState } from 'react';
import axios from 'axios';
import { baseAPI } from '../../config';
import { toast } from 'react-toastify';
import {
  DEBT_ACTIVITY_TYPES,
  DEBT_ACTIVITY_LABELS,
} from '@claimup/shared/constants/expedient';
import { PlusIcon } from '@heroicons/react/24/outline';

const DebtActivityAddForm = ({ expedientId, onSuccess }) => {
  const [formData, setFormData] = useState({
    type: DEBT_ACTIVITY_TYPES.DEBT,
    title: '',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [adding, setAdding] = useState(false);

  const activityTypes = Object.values(DEBT_ACTIVITY_TYPES);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.type || !formData.title || !formData.amount) {
      toast.error('Completa tipo, concepto e importe');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) {
      toast.error('El importe debe ser un número positivo');
      return;
    }

    try {
      setAdding(true);
      await axios.post(
        `${baseAPI}/expedients/${expedientId}/debt-activity`,
        {
          type: formData.type,
          title: formData.title,
          description: formData.description,
          amount: amount,
          date: formData.date,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      toast.success('Movimiento añadido');
      setFormData({
        type: DEBT_ACTIVITY_TYPES.DEBT,
        title: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
      });
      onSuccess?.();
    } catch (error) {
      console.error('Error adding debt activity:', error);
      toast.error(error.response?.data?.error || 'Error al añadir el movimiento');
    } finally {
      setAdding(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          >
            {activityTypes.map(type => (
              <option key={type} value={type}>
                {DEBT_ACTIVITY_LABELS[type]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[150px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Concepto</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Ej: Factura #12345"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div className="w-24">
          <label className="block text-xs font-medium text-gray-500 mb-1">Importe</label>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div className="w-32">
          <label className="block text-xs font-medium text-gray-500 mb-1">Fecha</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Descripción (opcional)</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Información adicional..."
          rows={2}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={adding}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          {adding ? 'Añadiendo...' : 'Añadir'}
        </button>
      </div>
    </form>
  );
};

export default DebtActivityAddForm;
